// Web Push subscribe/unsubscribe helpers.
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return new Uint8Array(raw.length).map((_, i) => raw.charCodeAt(i));
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPushStatus() {
  if (!pushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission: Notification.permission,        // "default" | "granted" | "denied"
    subscribed: !!sub,
  };
}

export async function subscribePush() {
  if (!pushSupported()) throw new Error("Push not supported on this browser/device.");
  const reg = await navigator.serviceWorker.ready;
  // Ask for permission if not yet granted.
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") throw new Error("Permission denied.");
  }
  if (Notification.permission !== "granted") {
    throw new Error("Notifications are blocked. Enable them in your browser settings.");
  }
  // Get VAPID public key from server.
  const { data: vapid } = await api.get("/push/vapid-public-key");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
  });
  // Persist on server.
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    expirationTime: json.expirationTime || null,
  });
  return sub;
}

export async function unsubscribePush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const json = sub.toJSON();
  try {
    await api.post("/push/unsubscribe", {
      endpoint: json.endpoint,
      keys: json.keys || { p256dh: "", auth: "" },
    });
  } catch (_) { /* server-side delete may already be gone */ }
  await sub.unsubscribe();
  return true;
}

export async function sendTestPush() {
  return (await api.post("/push/test")).data;
}
